import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const apiMessage = (error, fallback) =>
  error.response?.data?.error ||
  error.response?.data?.message ||
  error.message ||
  fallback;

export const fetchDeletionEligibility = createAsyncThunk(
  "account/fetchDeletionEligibility",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/account/deletion-eligibility");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not load eligibility."));
    }
  }
);

export const requestAccountDeletion = createAsyncThunk(
  "account/requestAccountDeletion",
  async ({ password, queueIfBlocked = false }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/account/request-deletion", { password, queueIfBlocked });
      return res.data;
    } catch (error) {
      return rejectWithValue({
        message: apiMessage(error, "Could not delete your account."),
        checks: error.response?.data?.checks,
      });
    }
  }
);

export const cancelAccountDeletion = createAsyncThunk(
  "account/cancelAccountDeletion",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/account/cancel-deletion");
      return res.data;
    } catch (error) {
      return rejectWithValue(apiMessage(error, "Could not cancel deletion."));
    }
  }
);

const accountSlice = createSlice({
  name: "account",
  initialState: {
    deletionEligibility: null,
    deletionEligibilityStatus: "idle",
    deletionActionStatus: "idle",
    deletionError: null,
  },
  reducers: {
    clearDeletionState: (state) => {
      state.deletionEligibility = null;
      state.deletionEligibilityStatus = "idle";
      state.deletionActionStatus = "idle";
      state.deletionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeletionEligibility.pending, (state) => {
        state.deletionEligibilityStatus = "loading";
        state.deletionError = null;
      })
      .addCase(fetchDeletionEligibility.fulfilled, (state, action) => {
        state.deletionEligibilityStatus = "succeeded";
        state.deletionEligibility = action.payload;
        state.deletionError = null;
      })
      .addCase(fetchDeletionEligibility.rejected, (state, action) => {
        state.deletionEligibilityStatus = "failed";
        state.deletionError = action.payload;
        state.deletionEligibility = null;
      })
      .addCase(requestAccountDeletion.pending, (state) => {
        state.deletionActionStatus = "loading";
        state.deletionError = null;
      })
      .addCase(requestAccountDeletion.fulfilled, (state) => {
        state.deletionActionStatus = "succeeded";
        state.deletionError = null;
      })
      .addCase(requestAccountDeletion.rejected, (state, action) => {
        state.deletionActionStatus = "failed";
        const payload = action.payload;
        state.deletionError =
          typeof payload === "string" ? payload : payload?.message || "Request failed.";
        if (payload?.checks) {
          state.deletionEligibility = {
            ...(state.deletionEligibility || {}),
            allowed: false,
            checks: payload.checks,
          };
        }
      })
      .addCase(cancelAccountDeletion.pending, (state) => {
        state.deletionActionStatus = "loading";
        state.deletionError = null;
      })
      .addCase(cancelAccountDeletion.fulfilled, (state) => {
        state.deletionActionStatus = "succeeded";
        state.deletionError = null;
      })
      .addCase(cancelAccountDeletion.rejected, (state, action) => {
        state.deletionActionStatus = "failed";
        state.deletionError = action.payload;
      });
  },
});

export const { clearDeletionState } = accountSlice.actions;
export default accountSlice.reducer;
